import { format } from "date-fns/format";
import { subDays } from "date-fns/subDays";
import React, { useEffect, useState } from "react";
import Select from "react-select";

import {
  CustomDropdownWithCalendar,
  DateRangePicker,
  MultiDatePicker,
} from "../../../components";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { customAnalysisFormSlice } from "../../../redux/slices/customAnalysisFormSlice";
import {
  customSelectStyles,
  dateRangeOptions,
  today,
} from "../../../shared/Constants";
import { SegmentDetails } from "../../../types";

type SegmentDateRangeProps = {
  segmentType: string;
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean | undefined,
  ) => void;
  setFieldTouched: (
    field: string,
    isTouched?: boolean | undefined,
    shouldValidate?: boolean | undefined,
  ) => void;
  segmentKey: string;
  currentSegmentState: SegmentDetails;
};

export const SegmentDateRange: React.FC<SegmentDateRangeProps> = ({
  segmentType,
  setFieldValue,
  setFieldTouched,
  segmentKey,
  currentSegmentState,
}: SegmentDateRangeProps): JSX.Element => {
  // State
  const [startDate, setStartDate] = useState<null | Date>(null);
  const [endDate, setEndDate] = useState<null | Date>(null);
  const [selectedDates, setSelectedDates] = useState<any[]>([]);

  // RTK slice actions
  const dispatch = useAppDispatch();
  const { storeCustomAnalysisFormState } = customAnalysisFormSlice.actions;

  // Redux calls
  const customAnalysisFormState = useAppSelector(
    (state) => state.customAnalysisForm,
  );
  const customAnalysisState = useAppSelector((state) => state.customAnalysis);

  useEffect(() => {
    if (
      segmentType === "primary" &&
      customAnalysisState.primarySegmentData.criteria.sessionDate
    ) {
      setStartDate(
        customAnalysisState.primarySegmentData.criteria.sessionDate[0]
          ? new Date(
              `${customAnalysisState.primarySegmentData.criteria.sessionDate[0]} 00:00:00`,
            )
          : null,
      );
      setEndDate(
        customAnalysisState.primarySegmentData.criteria.sessionDate[1]
          ? new Date(
              `${customAnalysisState.primarySegmentData.criteria.sessionDate[1]} 00:00:00`,
            )
          : null,
      );
    } else if (
      segmentType === "population" &&
      customAnalysisState.populationSegmentData.criteria.sessionDate
    ) {
      setStartDate(
        customAnalysisState.populationSegmentData.criteria.sessionDate[0]
          ? new Date(
              `${customAnalysisState.populationSegmentData.criteria.sessionDate[0]} 00:00:00`,
            )
          : null,
      );
      setEndDate(
        customAnalysisState.populationSegmentData.criteria.sessionDate[1]
          ? new Date(
              `${customAnalysisState.populationSegmentData.criteria.sessionDate[1]} 00:00:00`,
            )
          : null,
      );
    } else if (
      segmentType === "comparison" &&
      customAnalysisState.comparisonSegmentData.criteria.sessionDate
    ) {
      setStartDate(
        customAnalysisState.comparisonSegmentData.criteria.sessionDate[0]
          ? new Date(
              `${customAnalysisState.comparisonSegmentData.criteria.sessionDate[0]} 00:00:00`,
            )
          : null,
      );
      setEndDate(
        customAnalysisState.comparisonSegmentData.criteria.sessionDate[1]
          ? new Date(
              `${customAnalysisState.comparisonSegmentData.criteria.sessionDate[1]} 00:00:00`,
            )
          : null,
      );
    }
  }, [customAnalysisState, segmentType]);

  useEffect(() => {
    let dates: Date[] = [];
    if (
      segmentType === "primary" &&
      customAnalysisState.primarySegmentData.criteria.sessionDateIn
    ) {
      dates = customAnalysisState.primarySegmentData.criteria.sessionDateIn.map(
        (date: string) => new Date(`${date} 00:00:00`),
      );
    } else if (
      segmentType === "population" &&
      customAnalysisState.populationSegmentData.criteria.sessionDateIn
    ) {
      dates =
        customAnalysisState.populationSegmentData.criteria.sessionDateIn.map(
          (date: string) => new Date(`${date} 00:00:00`),
        );
    } else if (
      segmentType === "comparison" &&
      customAnalysisState.comparisonSegmentData.criteria.sessionDateIn
    ) {
      dates =
        customAnalysisState.comparisonSegmentData.criteria.sessionDateIn.map(
          (date: string) => new Date(`${date} 00:00:00`),
        );
    }
    setSelectedDates(dates);
  }, [customAnalysisState, segmentType]);

  const determineDateRange = (value: string) => {
    setStartDate(null);
    setEndDate(null);
    const currentDay = format(new Date(today), "yyyy-MM-dd");

    if (value === "Today") {
      return [currentDay];
    } else if (value === "Yesterday") {
      return [format(subDays(new Date(), 1), "yyyy-MM-dd")];
    } else if (value === "Last 7 Days") {
      return [format(subDays(new Date(), 7), "yyyy-MM-dd"), currentDay];
    } else if (value === "Last 14 Days") {
      return [format(subDays(new Date(), 14), "yyyy-MM-dd"), currentDay];
    } else if (value === "Last 30 Days") {
      return [format(subDays(new Date(), 30), "yyyy-MM-dd"), currentDay];
    } else if (value === "Last 60 Days") {
      return [format(subDays(new Date(), 60), "yyyy-MM-dd"), currentDay];
    } else if (value === "Last Year") {
      return [format(subDays(new Date(), 365), "yyyy-MM-dd"), currentDay];
    }
  };

  const handleDateRangeBlur = () => {
    setFieldTouched(`${segmentType}Segment.dateRange`, true);
  };

  const handleMultipleDatesBlur = () => {
    setFieldTouched(`${segmentType}Segment.dateRange`, true);
  };

  useEffect(() => {
    if (startDate && endDate) {
      const customDateRange = [
        format(startDate, "yyyy-MM-dd"),
        format(endDate, "yyyy-MM-dd"),
      ];
      setFieldValue(`${segmentType}Segment.dateRange`, customDateRange);
      dispatch(
        storeCustomAnalysisFormState({
          ...customAnalysisFormState,
          [segmentKey]: {
            ...currentSegmentState,
            dateRange: customDateRange,
            individualDates: [],
          },
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endDate, startDate]);

  useEffect(() => {
    if (selectedDates.length > 0) {
      const formattedDates = selectedDates.map((date) =>
        format(date, "yyyy-MM-dd"),
      );

      setFieldValue(`${segmentType}Segment.dateRange`, formattedDates);
      dispatch(
        storeCustomAnalysisFormState({
          ...customAnalysisFormState,
          [segmentKey]: {
            ...currentSegmentState,
            individualDates: formattedDates,
            dateRange: [],
          },
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDates]);

  return (
    <div data-testid="date-range-container">
      <label
        className="text-gray-900"
        data-testid={`${segmentType}-date-range-label`}
      >
        Date Range
      </label>

      <div className="flex">
        <div
          className="w-60 mt-4 mr-4"
          data-testid={`${segmentType}-date-range-dropdown-container`}
        >
          <Select
            name={`${segmentType}-date-range-dropdown`}
            aria-labelledby="date-range-dropdown-aria-label"
            inputId={`${segmentType}-date-range-dropdown`}
            options={dateRangeOptions}
            value={
              currentSegmentState.dateCriteria.value !== ""
                ? {
                    label: currentSegmentState.dateCriteria.value,
                    value: currentSegmentState.dateCriteria.label,
                  }
                : null
            }
            isSearchable={false}
            styles={customSelectStyles}
            classNamePrefix={`${segmentType}-date-range-dropdown`}
            components={{
              DropdownIndicator: CustomDropdownWithCalendar,
              IndicatorSeparator: () => null,
            }}
            placeholder="Select Date Range"
            onChange={(dateCriteria) => {
              if (dateCriteria) {
                const dateRange = determineDateRange(dateCriteria.label);
                setFieldValue(
                  `${segmentType}Segment.dateCriteria`,
                  dateCriteria,
                );
                setFieldValue(`${segmentType}Segment.dateRange`, dateRange);
                dispatch(
                  storeCustomAnalysisFormState({
                    ...customAnalysisFormState,
                    [segmentKey]: {
                      ...currentSegmentState,
                      dateCriteria: dateCriteria,
                      dateRange,
                      individualDates: [],
                    },
                  }),
                );
              }
            }}
          />
        </div>

        {currentSegmentState.dateCriteria.label === "Custom Range" && (
          <div className="w-60 mt-4">
            <DateRangePicker
              placeholderText={"Select Range"}
              startDate={startDate}
              setStartDate={setStartDate}
              endDate={endDate}
              setEndDate={setEndDate}
              segmentType={segmentType}
              onBlur={handleDateRangeBlur}
            />
          </div>
        )}

        {currentSegmentState.dateCriteria.label === "Individual Dates" && (
          <div className="w-60 mt-4">
            <MultiDatePicker
              selectedDates={selectedDates}
              setSelectedDates={setSelectedDates}
              segmentType={segmentType}
              onBlur={handleMultipleDatesBlur}
            />
          </div>
        )}
      </div>
    </div>
  );
};
